/**
 * Mixpanel Analytics Integration for FieldIDX
 * 
 * This module provides a centralized analytics layer with:
 * - Safe initialization (graceful failure if token missing)
 * - Typed event tracking
 * - User identification for athlete journey tracking
 * - Session replay (100% recording)
 * - Development mode logging
 */

import mixpanel from 'mixpanel-browser';
import { logger } from './logger';

// Check for Mixpanel token - available as env var in frontend
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const isDev = import.meta.env.DEV;

// Track initialization state
let isInitialized = false;

/**
 * Initialize Mixpanel with production-ready settings
 * Call this once at app startup
 */
export function initAnalytics(): void {
  if (!MIXPANEL_TOKEN) {
    if (isDev) {
      console.warn('[Analytics] VITE_MIXPANEL_TOKEN not set - analytics disabled');
    }
    return;
  }

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      autocapture: true,
      track_pageview: true,
      record_sessions_percent: 100,
      debug: isDev,
      persistence: 'localStorage',
      api_host: 'https://api-js.mixpanel.com',
    });

    isInitialized = true;

    if (isDev) {
      console.log('[Analytics] Mixpanel initialized successfully');
    }
  } catch (error) {
    logger.error('Failed to initialize Mixpanel', error);
  }
}

/**
 * Track a custom event with optional properties
 * 
 * @param eventName - Name of the event (e.g., 'Video Uploaded')
 * @param properties - Optional key-value properties
 */
/**
 * Sanitize properties to ensure valid JSON types only
 * Prevents Mixpanel 400 errors from complex objects like File, Blob, Error, Event
 */
function sanitizeProperties(props?: Record<string, unknown>): Record<string, string | number | boolean | null> {
  if (!props) return {};
  
  const sanitized: Record<string, string | number | boolean | null> = {};
  
  for (const [key, value] of Object.entries(props)) {
    // Skip known problematic types entirely
    if (value instanceof File) {
      sanitized[`${key}_name`] = value.name;
      sanitized[`${key}_size`] = value.size;
      sanitized[`${key}_type`] = value.type;
      continue;
    }
    if (value instanceof Blob) {
      sanitized[`${key}_size`] = value.size;
      sanitized[`${key}_type`] = value.type;
      continue;
    }
    if (value instanceof Error) {
      sanitized[`${key}_message`] = value.message;
      continue;
    }
    if (value instanceof Event || (typeof value === 'object' && value !== null && 'target' in value)) {
      sanitized[key] = '[Event]';
      continue;
    }
    
    // Only allow primitive types
    if (value === null || value === undefined) {
      sanitized[key] = null;
    } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value;
    } else if (Array.isArray(value)) {
      // Convert arrays to comma-separated strings (only primitives)
      sanitized[key] = value
        .filter(v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
        .map(v => String(v))
        .join(', ');
    } else if (typeof value === 'object') {
      // Check for circular references and complex objects
      try {
        // Test if it's JSON-serializable first
        const testStr = JSON.stringify(value);
        // Only include if it's reasonably small
        if (testStr.length <= 500) {
          sanitized[key] = testStr;
        } else {
          sanitized[key] = '[LargeObject]';
        }
      } catch {
        // Circular reference or non-serializable
        sanitized[key] = '[ComplexObject]';
      }
    } else {
      sanitized[key] = String(value);
    }
  }
  
  return sanitized;
}

export function trackEvent(
  eventName: string, 
  properties?: Record<string, unknown>
): void {
  const sanitizedProps = sanitizeProperties(properties);
  const finalPayload = {
    ...sanitizedProps,
    timestamp: new Date().toISOString(),
    source: 'web',
  };

  if (!isInitialized) {
    if (isDev) {
      console.log(`[Analytics] Would track event: "${eventName}"`, finalPayload);
    }
    return;
  }

  try {
    if (isDev) {
      console.log(`[Analytics] Sending payload for "${eventName}":`, JSON.stringify(finalPayload, null, 2));
    }

    mixpanel.track(eventName, finalPayload);

    if (isDev) {
      console.log(`[Analytics] ✅ Event tracked: "${eventName}"`);
    }
  } catch (error) {
    logger.error('Failed to track event', error, { eventName });
  }
}

/**
 * Identify a user for cross-session tracking
 * Links all future events to this user ID
 * 
 * @param userId - Unique user identifier (Supabase user.id)
 * @param traits - Optional user properties (name, email, sport, etc.)
 */
export function identifyUser(
  userId: string, 
  traits?: Record<string, unknown>
): void {
  // Ensure userId is a clean string
  const cleanUserId = typeof userId === 'string' ? userId : String(userId);
  
  if (!cleanUserId || cleanUserId === 'undefined' || cleanUserId === 'null') {
    if (isDev) {
      console.warn('[Analytics] Invalid userId provided to identifyUser:', userId);
    }
    return;
  }

  const sanitizedTraits = sanitizeProperties(traits);

  if (!isInitialized) {
    if (isDev) {
      console.log(`[Analytics] Would identify user: ${cleanUserId}`, sanitizedTraits);
    }
    return;
  }

  try {
    if (isDev) {
      console.log(`[Analytics] Identifying user: ${cleanUserId}`);
    }

    // Set the distinct_id for Mixpanel
    mixpanel.identify(cleanUserId);

    // Set user profile properties if provided
    if (traits && Object.keys(sanitizedTraits).length > 0) {
      mixpanel.people.set({
        ...sanitizedTraits,
        $last_login: new Date().toISOString(),
      });
    }

    if (isDev) {
      console.log(`[Analytics] ✅ User identified: ${cleanUserId}`, sanitizedTraits);
    }
  } catch (error) {
    logger.error('Failed to identify user', error, { userId: cleanUserId });
  }
}

/**
 * Reset analytics state on logout
 * Clears user identification and prepares for new session
 */
export function resetAnalytics(): void {
  if (!isInitialized) {
    if (isDev) {
      console.log('[Analytics] Would reset analytics');
    }
    return;
  }

  try {
    mixpanel.reset();

    if (isDev) {
      console.log('[Analytics] Analytics reset - user cleared');
    }
  } catch (error) {
    logger.error('Failed to reset analytics', error);
  }
}

/**
 * Track page view manually (in addition to auto-tracking)
 * 
 * @param pageName - Name of the page
 * @param properties - Optional additional properties
 */
export function trackPageView(
  pageName: string,
  properties?: Record<string, unknown>
): void {
  trackEvent('Page View', {
    page: pageName,
    url: window.location.href,
    path: window.location.pathname,
    ...properties,
  });
}

// ============================================
// Pre-defined Event Tracking Functions
// ============================================

/**
 * Track successful video upload
 */
export function trackVideoUpload(data: {
  videoId: string;
  athleteId: string;
  title: string;
  duration?: number;
  fileSize?: number;
}): void {
  trackEvent('Video Uploaded', {
    video_id: data.videoId,
    athlete_id: data.athleteId,
    title: data.title,
    duration_seconds: data.duration,
    file_size_bytes: data.fileSize,
  });
}

/**
 * Track NIL valuation calculation
 */
export function trackNILValuation(data: {
  athleteId: string;
  previousValue: number;
  newValue: number;
  changeReason: string;
  screenTimeSeconds?: number;
  uniqueScoutViews?: number;
}): void {
  trackEvent('NIL Valuation Calculated', {
    athlete_id: data.athleteId,
    previous_value: data.previousValue,
    new_value: data.newValue,
    value_change: data.newValue - data.previousValue,
    change_percent: data.previousValue > 0 
      ? ((data.newValue - data.previousValue) / data.previousValue * 100).toFixed(2)
      : 'N/A',
    reason: data.changeReason,
    screen_time_seconds: data.screenTimeSeconds,
    unique_scout_views: data.uniqueScoutViews,
  });
}

/**
 * Track subscription events
 */
export function trackSubscription(action: 'started' | 'upgraded' | 'canceled' | 'payment_failed', data: {
  tier?: string;
  amount?: number;
  trialDaysRemaining?: number;
}): void {
  trackEvent(`Subscription ${action.charAt(0).toUpperCase() + action.slice(1)}`, {
    ...data,
  });
}

/**
 * Track evaluation events
 */
export function trackEvaluation(action: 'requested' | 'completed' | 'viewed', data: {
  athleteId?: string;
  sport?: string;
  position?: string;
  score?: number;
}): void {
  trackEvent(`Evaluation ${action.charAt(0).toUpperCase() + action.slice(1)}`, {
    ...data,
  });
}

// Export the raw mixpanel instance for advanced use cases
export { mixpanel };
